import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import GoalContainer from "./usertypeContainer";
import UserTypeForm from "./usertypeform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchUserTypesThunk,
  addUserTypeThunk,
  updateUserTypeThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  userTypes: rbac.userTypes,
  loading: rbac.loading,
}));

const GoalPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { userTypes, loading } = useSelector(rbacSelector);

  const goalList = userTypes || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editGoalData, setEditGoalData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);

  const loadGoalList = useCallback(() => {
    return dispatch(fetchUserTypesThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUserTypesThunk());
  }, [dispatch]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedGoalId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        usertypeid: isEdit
          ? Yup.number().optional()
          : Yup.number()
            .required("User type ID is required")
            .max(9999, "Maximum 4 digits allowed")
            .test(
              "max-digits",
              "Maximum 4 digits allowed",
              function (value) {
                if (!value) return true;
                return value.toString().length <= 4;
              }
            ),
        usertypename: Yup.string()
          .required("User type name is required")
          .matches(/^[a-zA-Z/\s-]+$/, "User type name can only contain Characters")
          .max(70, "Maximum 70 characters allowed"),
        description: Yup.string()
          .required("Description is required")
          .matches(/^[a-zA-Z/\s-]+$/, "Description can only contain Characters")
          .max(100, "Maximum 100 characters allowed"),
        status: isEdit
          ? Yup.number().required("Status is required")
          : Yup.number().optional(),
      }),
    [isEdit]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      usertypeid: "",
      usertypename: "",
      description: "",
      status: 1,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();
      const editData = editGoalData;
      const isEditMode = isEdit;
      setIsEdit(false);
      setEditGoalData(null);

      try {
        if (isEditMode && editData) {
          const res = await dispatch(
            updateUserTypeThunk(editData.usertypeid, {
              usertypename: values.usertypename,
              description: values.description,
              status: values.status || 1,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to update user type");
            return;
          }
          toast.success("User type updated successfully!");
        } else {
          const res = await dispatch(
            addUserTypeThunk({
              usertypeid: Number(values.usertypeid) || 0,
              usertypename: values.usertypename,
              description: values.description,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to add user type");
            return;
          }
          toast.success("User type added successfully!");
        }

        await dispatch(fetchUserTypesThunk());
      } catch (err) {
        console.error("Error saving user type:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      setIsEdit(false);
      setEditGoalData(null);
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditGoalData(row);
      setIsModalOpen(true);

      validation.setValues({
        usertypeid: row.usertypeid || 0,
        usertypename: row.usertypename || "",
        description: row.description || "",
        status: row.status === "Active" ? 1 : 2,
      });
    },
    [validation]
  );

  const filteredGoals = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return goalList;
    return goalList.filter((t: any) =>
      t.usertypename?.toLowerCase().includes(q)
    );
  }, [goalList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "User Type ID",
        accessorKey: "usertypeid",
        cell: ({ row }: any) => {
          const id = row.original.usertypeid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "User Type Name", accessorKey: "usertypename" },
      { header: "Description", accessorKey: "description" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <ul className="list-inline d-flex justify-content-center align-items-center mb-0">
              <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li>
  
              {/* Delete */}
             {/* <li className="list-inline-item" title="Delete">
              <Button
                color=""
                className="btn btn-icon btn-soft-danger btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row.usertypeid);  
                }}
              > */}
                {/* <i className="ri-delete-bin-line"></i>
              </Button>
            </li> */}
            </ul>
          );
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="User Type" pageTitle={["RBAC", "User Types"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            {/* <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Category..."
                onChange={(e) => setSearchQuery(e.target.value)}
              /> */}
              {/* <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div> */}
          </Col>
          <Col md={9} className="text-end">
          <Button color="primary" onClick={toggleModal}>
          + Add New
        </Button>  
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredGoals.length > 0 ? (
                    <GoalContainer
                      columns={columns}
                      data={filteredGoals}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit User Type" : "Add New User Type"}
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <UserTypeForm validation={validation} isEdit={isEdit} />
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="primary"
            >
              {isEdit ? "Update" : "Add"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User Type"
        message="Are you sure you want to delete this user type?"
        onConfirm={async () => {
          if (selectedGoalId) {
            setDeleteModalOpen(false);
            const usertypeid = selectedGoalId;
            setSelectedGoalId(null);
            // Note: Delete API not implemented yet in service
            toast.info("Delete functionality not yet implemented");
            await dispatch(fetchUserTypesThunk());
          }
        }}
      />
    </div>
  );
};

export default GoalPage;
